import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationTypes } from 'src/app/app.enums';
import { ConnectionsService, TermsAcceptanceStatus } from 'src/app/Services/connections.service';
import { GalleriesService, GalleryDocumentDTO, GalleryItems, ResponseObject } from 'src/app/Services/galleries.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'terms-acceptance-dialog',
    templateUrl: './terms-acceptance-dialog.component.html',
    styleUrls: ['./terms-acceptance-dialog.component.css'],
    standalone: false
})
export class TermsAcceptanceDialogComponent implements OnInit, OnChanges {
    @Input() showDialog: boolean = false;
    @Input() galleryId: string = '';
    @Output() showDialogChange = new EventEmitter<boolean>();
    @Output() onAccepted = new EventEmitter<void>();
    @Output() onCancelled = new EventEmitter<void>();

    public galleryDetails: GalleryItems | null = null;
    public termsStatus: TermsAcceptanceStatus | null = null;
    public isTermsAccepted: boolean = false;

    constructor(
        private connectionsService: ConnectionsService,
        private galleriesService: GalleriesService,
        private notificationsService: NotificationsService,
        private ngxSpinnerService: NgxSpinnerService,
        private transloco: TranslocoService
    ) { }

    ngOnInit(): void {
    }

    // Handle input changes
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['showDialog'] && changes['showDialog'].currentValue === true && this.galleryId) {
            this.loadGalleryTerms();
        }
    }

    // Load gallery terms and status
    private loadGalleryTerms(): void {
        this.ngxSpinnerService.show();
        this.galleriesService.fetchById(this.galleryId).subscribe(
            (gallery) => {
                this.galleryDetails = gallery;
                this.connectionsService.termsAcceptance(this.galleryId).subscribe(
                    (response: ResponseObject) => {
                        if (response.Success) {
                            this.termsStatus = response.ResponseData;
                            this.isTermsAccepted = this.termsStatus?.IsAccepted ?? false;
                        }
                        this.ngxSpinnerService.hide();
                    },
                    (error) => {
                        console.error('Error loading terms status:', error);
                        this.ngxSpinnerService.hide();
                        this.notificationsService.showNotification(
                            this.transloco.translate('termsAcceptanceDialog.error'),
                            this.transloco.translate('termsAcceptanceDialog.failedToLoadTermsStatus'),
                            NotificationTypes.Error
                        );
                    }
                );
            },
            (error) => {
                console.error('Error loading gallery details:', error);
                this.ngxSpinnerService.hide();
                this.notificationsService.showNotification(
                    this.transloco.translate('termsAcceptanceDialog.error'),
                    this.transloco.translate('termsAcceptanceDialog.failedToLoadGalleryInformation'),
                    NotificationTypes.Error
                );
            }
        );
    }

    // Open document in new tab
    public viewDocument(filePath: string): void {
        if (filePath) {
            window.open(filePath, '_blank');
        }
    }

    // Accept terms
    public onAcceptTerms(): void {
        if (!this.isTermsAccepted) {
            this.notificationsService.showNotification(
                this.transloco.translate('termsAcceptanceDialog.warning'),
                this.transloco.translate('termsAcceptanceDialog.pleaseAcceptTerms'),
                NotificationTypes.Error
            );
            return;
        }

        this.ngxSpinnerService.show();
        this.connectionsService.acceptanceTerms(this.galleryId).subscribe(
            (response: ResponseObject) => {
                this.ngxSpinnerService.hide();
                if (response.Success) {
                    this.notificationsService.showNotification(
                        this.transloco.translate('termsAcceptanceDialog.success'),
                        this.transloco.translate('termsAcceptanceDialog.termsAcceptedSuccessfully'),
                        NotificationTypes.Success
                    );
                    this.onAccepted.emit();
                    this.closeDialog();
                } else {
                    this.notificationsService.showNotification(
                        this.transloco.translate('termsAcceptanceDialog.error'),
                        this.transloco.translate('termsAcceptanceDialog.failedToAcceptTerms'),
                        NotificationTypes.Error
                    );
                }
            },
            (error) => {
                console.error('Error accepting terms:', error);
                this.ngxSpinnerService.hide();
                this.notificationsService.showNotification(
                    this.transloco.translate('termsAcceptanceDialog.error'),
                    this.transloco.translate('termsAcceptanceDialog.errorAcceptingTerms'),
                    NotificationTypes.Error
                );
            }
        );
    }

    // Cancel dialog
    public onCancel(): void {
        this.onCancelled.emit();
        this.closeDialog();
    }

    // Close dialog
    public onDialogClose(): void {
        this.closeDialog();
    }

    // Reset dialog state
    private closeDialog(): void {
        this.showDialogChange.emit(false);
        this.isTermsAccepted = false;
        this.galleryDetails = null;
        this.termsStatus = null;
    }

    // Filter documents by section
    public getDocumentsBySection(section: number): GalleryDocumentDTO[] {
        if (!this.galleryDetails?.GalleryTermsHistroyDTO?.GalleryDocumentDTOs) {
            return [];
        }
        return this.galleryDetails.GalleryTermsHistroyDTO.GalleryDocumentDTOs.filter(
            doc => doc.Section === section
        );
    }
}